import type { ReactNode } from 'react';
import { Badge, Card, CardHeader, CardTitle, DataTable, type ColumnDef } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import type { LedgerEntry } from '../types';
import '../admin.css';

/** A refill is the plan's own credits, never a purchase (round 3); a pack is money in. */
const kindTone = {
  refill: 'neutral',
  pack: 'success',
  purchase: 'success',
  staff: 'warning',
  usage: 'neutral',
  adjustment: 'warning',
  refund: 'accent',
} as const;

/**
 * A page's read or understanding is a fraction of a credit, so a row shows four places and the
 * balance two (Dustin, 2026-09-30: whole credits showed every usage row as "-0").
 */
function columnsFor(f: Formatter): ColumnDef<LedgerEntry, unknown>[] {
  return [
    { accessorKey: 'at', header: 'When', cell: (c) => f.dateTime(c.row.original.at) },
    {
      accessorKey: 'kind',
      header: 'Kind',
      cell: (c) => <Badge tone={kindTone[c.row.original.kind]}>{c.row.original.kind}</Badge>,
    },
    {
      accessorKey: 'delta',
      header: 'Pages',
      cell: (c) => (
        <span className={`pui-num ${c.row.original.delta < 0 ? 'ad-danger' : ''}`}>
          {c.row.original.delta > 0 ? '+' : ''}
          {f.decimal(c.row.original.delta, 4)}
        </span>
      ),
    },
    {
      accessorKey: 'balance',
      header: 'Balance',
      cell: (c) => <span className="pui-num">{f.decimal(c.row.original.balance, 2)}</span>,
    },
    { accessorKey: 'reason', header: 'Reason', cell: (c) => c.row.original.reason ?? '' },
    { accessorKey: 'actor', header: 'By', cell: (c) => c.row.original.actor ?? 'Paige' },
  ];
}

/** The page ledger (credits before ADR-0030, 1 for 1) with purchases and referenced adjustments; Phone Home mirrors the adjustments. */
/** `balance` is the account reader's (one reader per number); the rows' running balance only without it. */
export function CreditLedger({
  entries,
  balance: given,
}: {
  entries: LedgerEntry[];
  balance?: number;
}): ReactNode {
  const f = useFormat();
  const balance = given ?? entries[0]?.balance ?? 0;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Page ledger</CardTitle>
        <span className={`ad-small ${balance <= 0 ? 'ad-danger' : 'pui-muted'}`}>
          {f.int(Math.trunc(balance))} pages left
        </span>
      </CardHeader>
      <DataTable
        caption="Page ledger"
        columns={columnsFor(f)}
        data={entries}
        dense
        getRowId={(e) => e.id}
      />
    </Card>
  );
}
